import { useEffect, useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  ShoppingCart,
  Package,
  Search,
  Plus,
  Check,
  Minus,
  FileText,
  Download,
  X,
  Clock,
  Globe,
  Eye,
  Layers,
  ZoomIn,
  Sparkles,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import { useCart } from '../hooks/useCart'
import type { Product, ProductVariant, Document } from '../types'
import { formatCurrency, sanitizeHtml } from '../utils/helpers'
import ProductDetailModal from '../components/ProductDetailModal'
import ShopAssistant from '../components/ShopAssistant'

const PAGE_SIZE = 50

export default function Catalog() {
  const { t } = useTranslation()
  const [products, setProducts] = useState<Product[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [orderFilter, setOrderFilter] = useState<'all' | 'purchased'>('all')
  const [addedProducts, setAddedProducts] = useState<Set<string>>(new Set())
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [docsModalProduct, setDocsModalProduct] = useState<Product | null>(null)
  const [productDocs, setProductDocs] = useState<Document[]>([])
  const [loadingDocs, setLoadingDocs] = useState(false)
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set())
  const [detailProduct, setDetailProduct] = useState<Product | null>(null)

  // AI assistant filter: when non-null, the grid shows these products instead of the paginated list
  const [aiProducts, setAiProducts] = useState<Product[] | null>(null)

  // Inline variant state: per-product map of loaded variants + selected id
  const [variantsByProduct, setVariantsByProduct] = useState<Record<string, ProductVariant[]>>({})
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({})
  // Track which products have already been fetched (avoid duplicate requests)
  const fetchingVariants = useRef<Set<string>>(new Set())

  const pageRef = useRef(1)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const { addToCart, getItemCount } = useCart()

  // Debounce search input (300 ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const getImageUrl = (baseUrl: string): string => {
    const token = localStorage.getItem('auth_token')
    const apiBase = import.meta.env.VITE_API_URL || '/api'
    const path = baseUrl.replace(/^\/api/, '')
    const fullUrl = apiBase + path
    if (!token) return fullUrl
    const sep = fullUrl.includes('?') ? '&' : '?'
    return `${fullUrl}${sep}token=${encodeURIComponent(token)}`
  }

  const loadFirstPage = useCallback(
    async (search: string, filter: 'all' | 'purchased') => {
      setLoading(true)
      setError(null)
      pageRef.current = 1
      try {
        const params: Parameters<typeof api.getProducts>[0] = { page: 1, limit: PAGE_SIZE }
        if (search) params.search = search
        if (filter === 'purchased') params.recently_purchased = 1
        const result = await api.getProducts(params)
        const items = result.data ?? []
        setProducts(items)
        setTotalCount(result.pagination.total)
        setHasMore(result.pagination.page < result.pagination.pages)
      } catch (err) {
        console.error('Erreur chargement produits:', err)
        setError(t('catalog.loadError'))
        setProducts([])
        setHasMore(false)
      } finally {
        setLoading(false)
      }
    },
    [t]
  )

  const loadNextPage = useCallback(async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    const nextPage = pageRef.current + 1
    try {
      const params: Parameters<typeof api.getProducts>[0] = { page: nextPage, limit: PAGE_SIZE }
      if (debouncedSearch) params.search = debouncedSearch
      if (orderFilter === 'purchased') params.recently_purchased = 1
      const result = await api.getProducts(params)
      const items = result.data ?? []
      setProducts((prev) => [...prev, ...items])
      pageRef.current = nextPage
      setHasMore(nextPage < result.pagination.pages)
    } catch (err) {
      console.error('Erreur chargement page suivante:', err)
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, hasMore, debouncedSearch, orderFilter])

  useEffect(() => {
    loadFirstPage(debouncedSearch, orderFilter).catch(console.error)
  }, [debouncedSearch, orderFilter, loadFirstPage])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore)
          loadNextPage().catch(console.error)
      },
      { threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
    // aiProducts: the sentinel unmounts while the AI filter is active — re-attach on clear
  }, [hasMore, loadingMore, loadNextPage, aiProducts])

  // When a new page of products loads, fetch variants for any product that has them
  // (also covers AI search results, which bypass the paginated list)
  useEffect(() => {
    for (const product of aiProducts ?? products) {
      if (!product.has_variants) continue
      if (fetchingVariants.current.has(product.id) || variantsByProduct[product.id]) continue
      fetchingVariants.current.add(product.id)
      api
        .getProductVariants(product.id)
        .then((data) => {
          setVariantsByProduct((prev) => ({ ...prev, [product.id]: data }))
          if (data.length > 0) {
            setSelectedVariants((prev) => ({ ...prev, [product.id]: data[0].id }))
          }
        })
        .catch(() => {
          setVariantsByProduct((prev) => ({ ...prev, [product.id]: [] }))
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, aiProducts])

  const getQuantity = (productId: string): number => quantities[productId] || 1

  const updateQuantity = (productId: string, value: number) => {
    if (value < 1) return
    setQuantities((prev) => ({ ...prev, [productId]: value }))
  }

  // Returns the effective price to display: variant price if selected, otherwise parent
  const getDisplayPrice = (product: Product): { price: number; price_ttc: number } => {
    if (!product.has_variants) return { price: product.price, price_ttc: product.price_ttc }
    const variantId = selectedVariants[product.id]
    const variant = (variantsByProduct[product.id] ?? []).find((v) => v.id === variantId)
    if (!variant) return { price: product.price, price_ttc: product.price_ttc }
    return {
      price: variant.price || product.price,
      price_ttc: variant.price_ttc || product.price_ttc,
    }
  }

  const handleViewDocs = async (product: Product) => {
    setDocsModalProduct(product)
    setLoadingDocs(true)
    try {
      const docs = await api.getProductDocuments(product.id)
      setProductDocs(docs)
    } catch (err) {
      console.error('Erreur chargement documents:', err)
      setProductDocs([])
    } finally {
      setLoadingDocs(false)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' o'
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' Ko'
    return (bytes / 1048576).toFixed(1) + ' Mo'
  }

  const handleAddToCart = (product: Product) => {
    if (product.has_variants) {
      const variantId = selectedVariants[product.id]
      const variant = (variantsByProduct[product.id] ?? []).find((v) => v.id === variantId)
      if (!variant) return
      const cartProduct: Product = {
        ...product,
        id: variant.id,
        ref: variant.ref || product.ref,
        label: `${product.label} — ${variant.label}`,
        price: variant.price || product.price,
        price_ttc: variant.price_ttc || product.price_ttc,
        has_variants: false,
      }
      addToCart(cartProduct, getQuantity(product.id))
    } else {
      addToCart(product, getQuantity(product.id))
    }
    setAddedProducts((prev) => new Set(prev).add(product.id))
    setTimeout(() => {
      setAddedProducts((prev) => {
        const s = new Set(prev)
        s.delete(product.id)
        return s
      })
    }, 2000)
  }

  // Products shown in the grid: AI filter result takes precedence over the paginated list
  const displayedProducts = aiProducts ?? products

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-gray-900">{t('catalog.title')}</h1>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={() => loadFirstPage(debouncedSearch, orderFilter)}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with cart */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('catalog.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('catalog.subtitle', { count: totalCount })}
          </p>
        </div>
        <Link
          to="/cart"
          className="relative flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <ShoppingCart className="w-5 h-5" />
          <span>{t('catalog.cart')}</span>
          {getItemCount() > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
              {getItemCount()}
            </span>
          )}
        </Link>
      </div>

      {/* Search + filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-sm font-medium flex-shrink-0">
            <button
              onClick={() => setOrderFilter('all')}
              className={`flex items-center gap-1.5 px-4 py-2 transition-colors ${orderFilter === 'all' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              {t('catalog.filterAll')}
            </button>
            <button
              onClick={() => setOrderFilter('purchased')}
              className={`flex items-center gap-1.5 px-4 py-2 transition-colors ${orderFilter === 'purchased' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <Clock className="w-3.5 h-3.5" />
              {t('catalog.filterPurchased')}
            </button>
          </div>
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('catalog.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>
        </div>
      </div>

      {/* AI assistant active filter banner */}
      {aiProducts !== null && (
        <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-xl px-4 py-3">
          <span className="flex items-center gap-2 text-sm text-primary-800">
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            {t('shopAssistant.activeFilter', { count: aiProducts.length })}
          </span>
          <button
            onClick={() => setAiProducts(null)}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline flex-shrink-0"
          >
            {t('shopAssistant.showAll')}
          </button>
        </div>
      )}

      {/* Products grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
          </div>
        ) : displayedProducts.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">{t('catalog.notFound')}</p>
          </div>
        ) : (
          displayedProducts.map((product) => {
            const displayPrice = getDisplayPrice(product)
            const productVariants = variantsByProduct[product.id]
            const variantsLoaded = productVariants !== undefined
            const selectedVariantId = selectedVariants[product.id] ?? ''

            return (
              <div
                key={product.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col h-full"
              >
                {/* Product image — click to open detail modal */}
                <div
                  className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden rounded-t-xl cursor-pointer group/img"
                  onClick={() => setDetailProduct(product)}
                >
                  {product.image && !brokenImages.has(product.id) ? (
                    <img
                      src={getImageUrl(product.image)}
                      alt={product.label}
                      className="w-full h-full object-contain p-2"
                      onError={() => setBrokenImages((prev) => new Set(prev).add(product.id))}
                    />
                  ) : (
                    <Package className="w-16 h-16 text-gray-400" />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover/img:opacity-100 transition-opacity drop-shadow" />
                  </div>
                </div>

                {/* Product info */}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">{product.label}</h3>
                      <p className="text-sm text-gray-500">
                        {t('catalog.ref', { ref: product.ref })}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        product.type === 0
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {product.type === 0 ? t('catalog.typeProduct') : t('catalog.typeService')}
                    </span>
                  </div>

                  {product.description && (
                    <div className="relative group/desc mb-4">
                      {product.description.includes('<') ? (
                        <div
                          className="text-sm text-gray-600 line-clamp-3 product-description cursor-default select-none"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }}
                        />
                      ) : (
                        <p className="text-sm text-gray-600 line-clamp-2 cursor-default select-none">
                          {product.description}
                        </p>
                      )}
                      <div className="absolute left-0 top-full mt-1 z-50 w-72 max-w-full opacity-0 invisible group-hover/desc:opacity-100 group-hover/desc:visible transition-opacity duration-150">
                        <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-3 max-h-52 overflow-y-auto text-sm text-gray-700 leading-relaxed">
                          {product.description.includes('<') ? (
                            <div
                              className="product-description"
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(product.description),
                              }}
                            />
                          ) : (
                            product.description
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Variant description — shown when a variant is selected and has its own description */}
                  {product.has_variants &&
                    (() => {
                      const selVariant = (variantsByProduct[product.id] ?? []).find(
                        (v) => v.id === selectedVariants[product.id]
                      )
                      return selVariant?.description ? (
                        <div className="mb-3 text-sm text-gray-500 italic border-l-2 border-primary-200 pl-2">
                          {selVariant.description.includes('<') ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(selVariant.description),
                              }}
                            />
                          ) : (
                            selVariant.description
                          )}
                        </div>
                      ) : null
                    })()}

                  {/* Inline variant selector */}
                  {product.has_variants && (
                    <div className="mb-4">
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1.5">
                        <Layers className="w-3.5 h-3.5" />
                        {t('catalog.selectVariant')}
                      </label>
                      {!variantsLoaded ? (
                        <div className="h-9 bg-gray-100 rounded-lg animate-pulse" />
                      ) : productVariants.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">{t('catalog.noVariants')}</p>
                      ) : (
                        <select
                          value={selectedVariantId}
                          onChange={(e) =>
                            setSelectedVariants((prev) => ({
                              ...prev,
                              [product.id]: e.target.value,
                            }))
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        >
                          {productVariants.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Price — only shown when price > 0, updates dynamically with selected variant */}
                  {displayPrice.price_ttc > 0 && (
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-gray-500">{t('catalog.priceExclTax')}</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(displayPrice.price)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">{t('catalog.priceInclTax')}</p>
                        <p className="text-lg font-bold text-primary-600">
                          {formatCurrency(displayPrice.price_ttc)}
                        </p>
                      </div>
                    </div>
                  )}

                  {product.stock !== null && product.stock !== undefined && (
                    <p
                      className={`text-sm mb-4 ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {product.stock > 0
                        ? t('catalog.inStock', { count: product.stock })
                        : t('catalog.outOfStock')}
                    </p>
                  )}

                  {product.url && (
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 px-3 py-1.5 mb-2 text-sm text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                    >
                      <Globe className="w-4 h-4" />
                      {t('catalog.productWebsite')}
                    </a>
                  )}

                  <button
                    onClick={() => handleViewDocs(product)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-1.5 mb-4 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-primary-600 transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    {t('catalog.documentation')}
                  </button>

                  {/* Quantity selector */}
                  <div className="flex items-center justify-center space-x-2 mb-4 mt-auto">
                    <button
                      onClick={() => updateQuantity(product.id, getQuantity(product.id) - 1)}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      disabled={getQuantity(product.id) <= 1}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={getQuantity(product.id)}
                      onChange={(e) => {
                        const value = parseInt(e.target.value)
                        if (!isNaN(value) && value >= 1) updateQuantity(product.id, value)
                      }}
                      className="w-20 text-center px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    />
                    <button
                      onClick={() => updateQuantity(product.id, getQuantity(product.id) + 1)}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <button
                    onClick={() => handleAddToCart(product)}
                    disabled={
                      product.stock === 0 ||
                      (product.has_variants && (!variantsLoaded || !selectedVariantId))
                    }
                    className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      addedProducts.has(product.id)
                        ? 'bg-green-600 text-white'
                        : product.stock === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    {addedProducts.has(product.id) ? (
                      <>
                        <Check className="w-5 h-5" />
                        <span>{t('catalog.addedToCart')}</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5" />
                        <span>{t('catalog.addToCart')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Infinite scroll sentinel — disabled while the AI filter is active */}
      {aiProducts === null && (
        <div ref={sentinelRef} className="flex justify-center py-6">
          {loadingMore && (
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
              {t('catalog.loadingMore')}
            </div>
          )}
          {!hasMore && products.length > 0 && !loadingMore && (
            <p className="text-sm text-gray-400">{t('catalog.noMoreProducts')}</p>
          )}
        </div>
      )}

      {/* AI shop assistant (chatbot + voice bot) */}
      <ShopAssistant
        onSearch={async (query) => {
          const result = await api.aiSearchProducts(query)
          setAiProducts(result.data)
          return { total: result.total, keywords: result.keywords }
        }}
        onClear={() => setAiProducts(null)}
      />

      {/* Product detail modal */}
      {detailProduct &&
        (() => {
          const p = detailProduct
          const pVariants = variantsByProduct[p.id]
          const selVarId = selectedVariants[p.id] ?? ''
          const selVariant = (pVariants ?? []).find((v) => v.id === selVarId)
          return (
            <ProductDetailModal
              product={p}
              onClose={() => setDetailProduct(null)}
              imageNode={
                p.image && !brokenImages.has(p.id) ? (
                  <img
                    src={getImageUrl(p.image)}
                    alt={p.label}
                    className="w-full h-full object-contain p-4"
                    onError={() => setBrokenImages((prev) => new Set(prev).add(p.id))}
                  />
                ) : (
                  <Package className="w-24 h-24 text-gray-400" />
                )
              }
              quantity={getQuantity(p.id)}
              onQuantityChange={(qty) => updateQuantity(p.id, qty)}
              onAddToCart={() => {
                handleAddToCart(p)
              }}
              isAdded={addedProducts.has(p.id)}
              variants={pVariants === undefined ? null : pVariants}
              selectedVariantId={selVarId}
              onVariantChange={(id) => setSelectedVariants((prev) => ({ ...prev, [p.id]: id }))}
              variantDescription={selVariant?.description}
              onViewDocs={() => handleViewDocs(p)}
            />
          )
        })()}

      {/* Modal documentation produit */}
      {docsModalProduct && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('catalog.docsModalTitle')}
                </h3>
                <p className="text-sm text-gray-500">
                  {docsModalProduct.label} ({docsModalProduct.ref})
                </p>
              </div>
              <button
                onClick={() => {
                  setDocsModalProduct(null)
                  setProductDocs([])
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {loadingDocs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : productDocs.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                  <p className="text-gray-500">{t('catalog.noDocsAvailable')}</p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {productDocs.map((doc, index) => {
                    const viewableTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']
                    const isViewable = viewableTypes.includes((doc.type || '').toLowerCase())
                    const filePath = doc.full_path || doc.path
                    return (
                      <li
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-5 h-5 text-primary-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isViewable && (
                            <button
                              onClick={() => api.viewDocument(filePath)}
                              className="p-2 text-gray-500 hover:text-primary-600 rounded-lg hover:bg-white transition-colors"
                              title={t('catalog.viewDoc')}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => api.downloadDocument(filePath)}
                            className="p-2 text-gray-500 hover:text-primary-600 rounded-lg hover:bg-white transition-colors"
                            title={t('catalog.downloadDoc')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
